import { lazy, PropsWithChildren, Suspense, useEffect, useState } from "react";
import Step1BookingDetails from "./widget/Step1BookingDetails";
import Step2CustomerDetails from "./widget/Step2CustomerDetails";
import SingleStepEnquiryView from "./widget/SingleStepEnquiryView";
import { useAtom } from "jotai";
import Loading from "./ui/Loading";
import Header from "./widget/Header";
import PaymentConfirmation from "./widget/PaymentConfirmation";
import PaymentFailed from "./widget/PaymentFailed";
import PaymentPending from "./widget/PaymentPending";
import { currentStepAtom, orderStatusAtom } from "./widget/store";
import { useListingQuery } from "../api/hooks";
import { useCampaignValidation } from "./widget/useCampaignValidation";
import useSyncQueryParams from "./widget/useSyncQueryParams";
import { useWidgetTheme } from "./widget/useWidgetTheme";
import { PaymentGateway, Query, WidgetMode } from "@repo/rq-api-public";
import { getPayFieldsScriptUrl, loadPayFieldsScript } from "@repo/worldpay";

const Step3PaymentDetailsWorldPay = lazy(
  () => import("./widget/Step3PaymentDetailsWorldPay"),
);
const Step3PaymentDetailsAuthorizeNet = lazy(
  () => import("./widget/Step3PaymentDetailsAuthorizeNet"),
);
const Step3PaymentDetailsStripe = lazy(
  () => import("./widget/Step3PaymentDetailsStripe"),
);

export default function Widget() {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [orderStatus, setOrderStatus] = useAtom(orderStatusAtom);
  const [, setCurrentStep] = useAtom(currentStepAtom);
  // Sync step with atom
  const handleSetStep = (newStep: 1 | 2 | 3) => {
    setStep(newStep);
    setCurrentStep(newStep);
  };

  useSyncQueryParams(handleSetStep);
  useWidgetTheme();
  useCampaignValidation(); // Only validates if campaign code exists

  const { data: listing, isLoading, isError } = useListingQuery();
  const isEnquiryMode = listing?.widgetMode === WidgetMode.Enquiry;

  const { data: paymentProvider } =
    Query.usePlatformAccountPaymentGatewayGETQuery();

  // Preload Payrix SDK as soon as we know it's a WorldPay account
  useEffect(() => {
    if (paymentProvider?.paymentGateway === PaymentGateway.WorldPay) {
      loadPayFieldsScript(
        getPayFieldsScriptUrl(import.meta.env.VITE_SENTRY_ENVIRONMENT),
      );
    }
  }, [paymentProvider?.paymentGateway]);

  if (isLoading) {
    return (
      <Layout showInfo={false}>
        <div className="h-full flex items-center justify-center">
          <Loading />
        </div>
      </Layout>
    );
  }

  if (isError) {
    return <Layout showInfo={false}>Error loading boat</Layout>;
  }

  if (orderStatus === "SUCCESS") {
    return (
      <Layout>
        <PaymentConfirmation />
      </Layout>
    );
  }

  if (orderStatus === "PENDING") {
    return (
      <Layout>
        <PaymentPending />
      </Layout>
    );
  }

  if (orderStatus === "PAYMENT_FAILED" || orderStatus === "SLOT_BOOKED") {
    return (
      <Layout>
        <PaymentFailed
          onTryAgain={() => {
            setOrderStatus(null);

            if (orderStatus === "SLOT_BOOKED") {
              handleSetStep(1);
            }
          }}
        />
      </Layout>
    );
  }

  if (isEnquiryMode) {
    return <SingleStepEnquiryView />;
  }

  return (
    <Layout>
      {step === 1 && (
        <Step1BookingDetails handleNextStep={() => handleSetStep(2)} />
      )}
      {step === 2 && (
        <Step2CustomerDetails
          handlePrevStep={() => handleSetStep(1)}
          handleNextStep={() => handleSetStep(3)}
        />
      )}
      {step === 3 && (
        <Suspense
          fallback={
            <div className="h-full flex items-center justify-center">
              <Loading />
            </div>
          }
        >
          {paymentProvider?.paymentGateway === PaymentGateway.AuthorizeNet && (
            <Step3PaymentDetailsAuthorizeNet
              handlePrevStep={() => handleSetStep(2)}
            />
          )}
          {paymentProvider?.paymentGateway === PaymentGateway.Stripe && (
            <Step3PaymentDetailsStripe
              handlePrevStep={() => handleSetStep(2)}
            />
          )}
          {paymentProvider?.paymentGateway === PaymentGateway.WorldPay && (
            <Step3PaymentDetailsWorldPay
              handlePrevStep={() => handleSetStep(2)}
            />
          )}
        </Suspense>
      )}
    </Layout>
  );
}

const Layout = ({
  children,
  showInfo,
  showHeader = true,
}: PropsWithChildren<{ showInfo?: boolean; showHeader?: boolean }>) => {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-full max-w-5xl bg-white md:rounded-3xl min-h-screen md:min-h-[unset]">
        {showHeader && <Header showBoatInfo={showInfo} />}

        <div className="md:p-5 pt-6">
          <div className="md:max-h-[670px] md:h-[670px] md:overflow-hidden">
            <div className="h-full overflow-auto">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
